import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, keys, useHelpTexts } from '../../api.ts';
import { Loading, PageHeader, useDocumentTitle, useToast } from '../../components/ui.tsx';

/** Sovelluksessa käytetyt ohjeiden nimet (samat kuin vanhassa järjestelmässä) */
export const HELP_KEYS: Array<[string, string]> = [
  ['Kirjautuminen', 'Kirjautumissivu'],
  ['Rekisteröinti', 'Rekisteröityminen'],
  ['OmatTiedot', 'Käyttäjän tiedot'],
  ['KayttajanMuokkaus', 'Tietojen muokkaus'],
  ['KayttajanMuokkausOHCW', 'Kiinteät OH-CW- ja Divisions-arvot'],
  ['OmatFixed', 'Kiinteät Band-Mode-arvot'],
  ['OmatMarathon', 'Kiinteät Marathon-arvot'],
  ['OmaQSO', 'QSO-lista'],
  ['Matriisi', 'DXCC-taulukko'],
  ['KayttajatLista', 'Käyttäjälista'],
  ['YksittainenQSO', 'Yksittäisen QSO:n lisäys'],
  ['Massalisäys', 'ADIF-massalisäys'],
  ['HakuLomake', 'Taulukkohaku'],
  ['KilpailuunOsallistuminen', 'Kilpailuun osallistuminen'],
];

export function HelpAdminPage() {
  useDocumentTitle('Ohjetekstit');
  const help = useHelpTexts();
  const qc = useQueryClient();
  const toast = useToast();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  useEffect(() => {
    if (help.data) setDrafts(help.data);
  }, [help.data]);
  const save = useMutation({
    mutationFn: async () => {
      const changed = Object.entries(drafts).filter(([k, v]) => (help.data?.[k] ?? '') !== v);
      for (const [name, text] of changed) await api.put(`/help/${encodeURIComponent(name)}`, { text });
      return changed.length;
    },
    onSuccess: (n) => {
      toast.ok(`${n} ohjetta tallennettu`);
      void qc.invalidateQueries({ queryKey: keys.help });
    },
    onError: toast.error,
  });

  if (help.isLoading) return <Loading />;
  const names = [...new Set([...HELP_KEYS.map(([k]) => k), ...Object.keys(help.data ?? {})])];
  const label = (k: string) => HELP_KEYS.find(([n]) => n === k)?.[1] ?? k;

  return (
    <div className="page page-narrow">
      <PageHeader
        title="Ohjetekstit"
        sub="Näkyvät sivuilla ?-painikkeen takana. Ohjeita voi muokata myös suoraan sivuilta."
        actions={
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
            Tallenna muutokset
          </button>
        }
      />
      <div className="stack">
        {names.map((k) => (
          <div key={k} className="field">
            <label htmlFor={`h-${k}`}>
              {label(k)} <span className="faint mono">({k})</span>
            </label>
            <textarea id={`h-${k}`} rows={4} value={drafts[k] ?? ''} onChange={(e) => setDrafts((d) => ({ ...d, [k]: e.target.value }))} />
          </div>
        ))}
      </div>
    </div>
  );
}
